import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface SystemStatus {
  database: 'online' | 'offline' | 'checking';
  api: 'online' | 'offline' | 'checking';
  lastSync: Date | null;
}

// Global cache to prevent multiple checks across component mounts
let globalStatus: SystemStatus | null = null;
let isChecking = false;
let hasCheckedThisSession = false;
const statusListeners: ((status: SystemStatus) => void)[] = [];

// Notify all components when status changes
const notifyStatusChange = (newStatus: SystemStatus) => {
  globalStatus = newStatus;
  statusListeners.forEach(listener => listener(newStatus));
};

export function useSystemStatus() {
  const [status, setStatus] = useState<SystemStatus>(() => {
    // Use cached status if available
    if (globalStatus) {
      return globalStatus;
    }
    return {
      database: 'checking', // Default to checking until manually verified
      api: 'checking', // Default to checking until manually verified
      lastSync: null
    };
  });

  // Subscribe to status changes
  useEffect(() => {
    const listener = (newStatus: SystemStatus) => {
      setStatus(newStatus);
    };
    
    statusListeners.push(listener);
    
    return () => {
      const index = statusListeners.indexOf(listener);
      if (index > -1) {
        statusListeners.splice(index, 1);
      }
    };
  }, []);

  const checkDatabaseStatus = async () => {
    try {
      const { error } = await supabase.from('devices').select('id').limit(1);
      return error ? 'offline' : 'online';
    } catch {
      return 'offline';
    }
  };

  const checkApiStatus = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/device-status?serial=TEST`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        }
      });
      return response.ok ? 'online' : 'offline';
    } catch {
      return 'offline';
    }
  };

  const checkSystemStatus = async () => {
    // Prevent multiple simultaneous checks
    if (isChecking) return;
    
    isChecking = true;
    const newStatus = { ...status, database: 'checking' as const, api: 'checking' as const };
    notifyStatusChange(newStatus);
    
    try {
      const [dbStatus, apiStatus] = await Promise.all([
        checkDatabaseStatus(),
        checkApiStatus()
      ]);

      const finalStatus = {
        database: dbStatus,
        api: apiStatus,
        lastSync: new Date()
      };

      notifyStatusChange(finalStatus);
      hasCheckedThisSession = true;
    } finally {
      isChecking = false;
    }
  };

  return { status, refreshStatus: checkSystemStatus };
}